import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api/client";
import { Result, PaginatedResponse } from "../types";

function Spinner() {
  return (
    <div className="flex justify-center py-12">
      <div className="w-10 h-10 border-[3px] border-indigo-100 border-t-indigo-600 rounded-full animate-spin" />
    </div>
  );
}

function ScoreBadge({ score }: { score: number }) {
  const cls =
    score > 140
      ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20"
      : score >= 80
      ? "bg-amber-50 text-amber-700 ring-1 ring-amber-600/20"
      : "bg-rose-50 text-rose-700 ring-1 ring-rose-600/20";
  return (
    <span className={`inline-block rounded-full px-3 py-1 text-sm font-semibold ${cls}`}>
      {score}
    </span>
  );
}

const YEARS = [2025, 2024, 2023, 2022, 2021];
const ETYPES = [
  { value: "", label: "Barchasi" },
  { value: "Kunduzgi", label: "Kunduzgi" },
  { value: "Sirtqi", label: "Sirtqi" },
  { value: "Kechki", label: "Kechki" },
  { value: "Masofaviy", label: "Masofaviy" },
];

type SortField = "grand_ball" | "kontrakt_ball" | "direction_name" | "institute_name";
type SortDir = "asc" | "desc";

const selectCls = "border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow";

export default function ResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [year, setYear] = useState(Number(searchParams.get("year")) || 2025);
  const [region, setRegion] = useState(searchParams.get("region") || "");
  const [institute, setInstitute] = useState(searchParams.get("institute") || "");
  const [etype, setEtype] = useState(searchParams.get("etype") || "");
  const [language, setLanguage] = useState(searchParams.get("language") || "");
  const [ballMin, setBallMin] = useState(searchParams.get("ball_min") || "");
  const [ballMax, setBallMax] = useState(searchParams.get("ball_max") || "");
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>("kontrakt_ball");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const [regions, setRegions] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [data, setData] = useState<PaginatedResponse<Result> | null>(null);
  const [loading, setLoading] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  useEffect(() => {
    api.get("/filters/regions").then((r) => setRegions(r.data || [])).catch(() => {});
    api.get("/filters/languages").then((r) => setLanguages(r.data || [])).catch(() => {});
  }, []);

  const fetchData = useCallback(
    (p: number) => {
      setLoading(true);
      const params: Record<string, string | number> = { year, page: p, size: 20 };
      if (search.trim()) params.search = search.trim();
      if (region) params.region = region;
      if (institute.trim()) params.institute = institute.trim();
      if (etype) params.etype = etype;
      if (language) params.language = language;
      if (ballMin) params.ball_min = Number(ballMin);
      if (ballMax) params.ball_max = Number(ballMax);
      params.sort = sortField;
      params.order = sortDir;

      const sp = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => sp.set(k, String(v)));
      setSearchParams(sp, { replace: true });

      api
        .get("/results", { params })
        .then((r) => {
          setData(r.data);
          setPage(p);
        })
        .catch(() => setData(null))
        .finally(() => setLoading(false));
    },
    [search, year, region, institute, etype, language, ballMin, ballMax, sortField, sortDir, setSearchParams]
  );

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchData(1);
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  useEffect(() => {
    if (data) fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDir]);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg className="w-3.5 h-3.5 text-slate-300 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return (
      <svg className="w-3.5 h-3.5 text-indigo-600 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {sortDir === "asc" ? (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        )}
      </svg>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">Natijalar</h1>
          <p className="text-slate-500 text-sm">O'tish ballari bo'yicha barcha natijalar</p>
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-slate-100 mb-8 overflow-hidden">
        <button
          type="button"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          className="w-full flex items-center justify-between px-5 sm:px-6 py-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filtrlar
          </span>
          <svg className={`w-5 h-5 text-slate-400 transition-transform ${filtersExpanded ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {filtersExpanded && (
          <div className="px-5 sm:px-6 pb-5 sm:pb-6 border-t border-slate-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-4">
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Yo'nalish nomi</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Yo'nalish nomini qidiring..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={selectCls + " w-full pl-9"}
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Universitet nomi</label>
                <input
                  type="text"
                  placeholder="Universitet nomini qidiring..."
                  value={institute}
                  onChange={(e) => setInstitute(e.target.value)}
                  className={selectCls + " w-full"}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Yil</label>
                <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={selectCls + " w-full"}>
                  {YEARS.map((y) => (<option key={y} value={y}>{y}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Hudud</label>
                <select value={region} onChange={(e) => setRegion(e.target.value)} className={selectCls + " w-full"}>
                  <option value="">Barcha hududlar</option>
                  {regions.map((r) => (<option key={r} value={r}>{r}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Ta'lim shakli</label>
                <select value={etype} onChange={(e) => setEtype(e.target.value)} className={selectCls + " w-full"}>
                  {ETYPES.map((et) => (<option key={et.value} value={et.value}>{et.label}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Ta'lim tili</label>
                <select value={language} onChange={(e) => setLanguage(e.target.value)} className={selectCls + " w-full"}>
                  <option value="">Barcha tillar</option>
                  {languages.map((l) => (<option key={l} value={l}>{l}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Min ball</label>
                <input
                  type="number"
                  min={0}
                  max={256}
                  placeholder="0"
                  value={ballMin}
                  onChange={(e) => setBallMin(e.target.value)}
                  className={selectCls + " w-full"}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Max ball</label>
                <input
                  type="number"
                  min={0}
                  max={256}
                  placeholder="256"
                  value={ballMax}
                  onChange={(e) => setBallMax(e.target.value)}
                  className={selectCls + " w-full"}
                />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <button
                type="submit"
                className="bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 text-white font-semibold px-8 py-3 rounded-xl transition-all shadow-sm shadow-indigo-200"
              >
                Qidirish
              </button>
            </div>
          </div>
        )}
      </form>

      {/* Results */}
      {loading ? (
        <Spinner />
      ) : data && data.items.length > 0 ? (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-500">
              Jami <span className="font-bold text-slate-800">{data.total}</span> ta natija
            </p>
          </div>

          {/* Desktop table */}
          <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-100">
                    <th
                      className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none"
                      onClick={() => toggleSort("direction_name")}
                    >
                      Yo'nalish <SortIcon field="direction_name" />
                    </th>
                    <th
                      className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none"
                      onClick={() => toggleSort("institute_name")}
                    >
                      Universitet <SortIcon field="institute_name" />
                    </th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Shakl</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Til</th>
                    <th
                      className="text-center p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none"
                      onClick={() => toggleSort("grand_ball")}
                    >
                      Grant <SortIcon field="grand_ball" />
                    </th>
                    <th
                      className="text-center p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none"
                      onClick={() => toggleSort("kontrakt_ball")}
                    >
                      Kontrakt <SortIcon field="kontrakt_ball" />
                    </th>
                    <th className="text-center p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Kvota</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.items.map((item, i) => (
                    <tr key={`${item.id}-${i}`} className="hover:bg-slate-50/50 transition-colors">
                      <td className="p-4 font-medium text-slate-900 max-w-[250px]">{item.direction_name}</td>
                      <td className="p-4 text-slate-600 max-w-[200px] truncate">{item.institute_name}</td>
                      <td className="p-4">
                        <span className="bg-indigo-50 text-indigo-700 rounded-lg px-2 py-0.5 text-xs font-medium">{item.etype}</span>
                      </td>
                      <td className="p-4 text-slate-600 text-xs">{item.language}</td>
                      <td className="p-4 text-center"><ScoreBadge score={item.grand_ball} /></td>
                      <td className="p-4 text-center"><ScoreBadge score={item.kontrakt_ball} /></td>
                      <td className="p-4 text-center text-slate-500 text-xs">
                        <span className="font-medium text-slate-700">{item.grand_kvota}</span> / <span className="font-medium text-slate-700">{item.kontrakt_kvota}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden space-y-3">
            {data.items.map((item, i) => (
              <div key={`${item.id}-${i}`} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4">
                <h3 className="font-semibold text-slate-900 text-sm mb-1 leading-snug">{item.direction_name}</h3>
                <p className="text-xs text-slate-500 mb-3 truncate">{item.institute_name}</p>
                <div className="flex items-center gap-2 mb-3">
                  <div className="text-center">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Grant</p>
                    <ScoreBadge score={item.grand_ball} />
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Kontrakt</p>
                    <ScoreBadge score={item.kontrakt_ball} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 text-xs">
                  <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-lg font-medium">{item.etype}</span>
                  <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-lg font-medium">{item.language}</span>
                  <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-lg font-medium">{item.region_name}</span>
                  <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-lg font-medium">Kvota: {item.grand_kvota}/{item.kontrakt_kvota}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {data.pages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-8">
              <button
                disabled={page <= 1}
                onClick={() => fetchData(page - 1)}
                className="flex items-center gap-1 px-4 py-2.5 rounded-xl border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                Oldingi
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(data.pages, 5) }, (_, i) => {
                  let pageNum: number;
                  if (data.pages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= data.pages - 2) {
                    pageNum = data.pages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => fetchData(pageNum)}
                      className={`w-10 h-10 rounded-xl text-sm font-semibold transition-all ${
                        page === pageNum
                          ? "bg-indigo-600 text-white shadow-sm"
                          : "text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                disabled={page >= data.pages}
                onClick={() => fetchData(page + 1)}
                className="flex items-center gap-1 px-4 py-2.5 rounded-xl border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700"
              >
                Keyingi
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-20">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-slate-700 text-lg font-semibold mb-1">Ma'lumot topilmadi</p>
          <p className="text-slate-400 text-sm">Filtrlarni o'zgartirib qaytadan urinib ko'ring</p>
        </div>
      )}
    </div>
  );
}
